/**
 * The banner that will not let you forget whose data you are looking at.
 *
 * ## Only the banner — the switcher is in the ☰ menu
 *
 * Picking an account is a setting and belongs in a menu. Being *in* that mode is
 * not a setting: it is a state that, forgotten, is how an admin edits the wrong
 * person's journal. So it is full-width, in a colour used nowhere else in the
 * app, it names the account, and leaving is one click that is always visible.
 * Nothing here can be collapsed, dismissed or scrolled past, which is the whole
 * reason it is a separate component from the control that turns it on.
 *
 * ## The server decides that this is happening, not the client
 *
 * `me.actingAs` is non-null only when qv-data actually honoured the `X-Act-As`
 * header — admin caller, test-user target, never another admin (D31). Reading
 * `localStorage` instead would show the banner for an impersonation the server
 * is refusing, which is the one lie this component must never tell.
 *
 * ## Stopping reloads the page
 *
 * Deliberately, and it is not laziness. Changing the account changes what every
 * open surface should show — the workspace, the journal, two sockets and a
 * screener — and each of those loads on mount. A reload is one line and is
 * *correct*; threading invalidation through all of them would be a lot of code
 * whose failure mode is a chart still showing the previous user's symbol.
 */

import { useEffect, useState } from "react";

import { setActingAs } from "../lib/act-as";
import { fetchMe, type Me } from "../api/me";

export function ActingAsBar() {
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    let alive = true;
    void fetchMe().then((found) => {
      if (alive) setMe(found);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Not acting as anyone — which covers not signed in, not an admin, and a
  // server that did not answer. In all of them there is nothing to warn about.
  if (!me?.actingAs) return null;

  // Acting as a group's own account is authoring, not viewing somebody's data,
  // and what matters is where a Publish lands — so the banner names the group
  // and its key (`docs/plans/group-accounts.md`, the mockup's wording).
  if (me.publishTo) {
    const group = me.groupAccounts.find((g) => g.key === me.actingAs);
    return (
      <div style={BANNER}>
        <span>
          Authoring as <strong>{group?.name ?? me.publishTo}</strong> — publishes go to{" "}
          <strong>{me.publishTo}</strong>
        </span>
        <button
          type="button"
          onClick={() => {
            setActingAs(null);
            window.location.reload();
          }}
          style={STOP}
        >
          Exit
        </button>
      </div>
    );
  }

  return (
    <div style={BANNER}>
      <span>
        Viewing <strong>{me.actingAs}</strong>&apos;s data — you are signed in as{" "}
        {me.actorKey}
      </span>
      <button
        type="button"
        onClick={() => {
          setActingAs(null);
          window.location.reload();
        }}
        style={STOP}
      >
        Stop
      </button>
    </div>
  );
}

const BANNER: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.8rem",
  padding: "0.3rem 0.8rem",
  // A colour used nowhere else in the app, because the whole job of this bar is
  // to be impossible to stop noticing.
  background: "#8a5a00",
  color: "#fff",
  fontSize: "0.78rem",
  flex: "0 0 auto",
};

const STOP: React.CSSProperties = {
  padding: "0.1rem 0.5rem",
  background: "rgba(255,255,255,0.18)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.35)",
  borderRadius: 4,
  font: "inherit",
  fontSize: "0.72rem",
  cursor: "pointer",
};
